import React, { useState, useEffect, useRef } from 'react';
import { Bot, Cpu, Keyboard, BookOpen, Globe, FileText, HelpCircle, Package, ShieldCheck, UploadCloud, Eye, EyeOff, Trash2, FolderOpen } from 'lucide-react';
import '../../styles-wa/ai_config.css';
import apiClient from '../../api-wa/client';

const MODEL_OPTIONS_BY_PROVIDER = {
  openai: [
    { value: 'gpt-4o-mini', label: 'GPT-4o Mini (Fast & Efficient)' },
    { value: 'gpt-4o', label: 'GPT-4o (High Intelligence)' },
    { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
    { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo' },
  ],
  groq: [
    { value: 'llama-3.3-70b-versatile', label: 'Llama 3.3 70B Versatile (Ultra Fast)' },
    { value: 'llama-3.1-8b-instant', label: 'Llama 3.1 8B Instant' },
    { value: 'mixtral-8x7b-32768', label: 'Mixtral 8x7B 32k' },
    { value: 'gemma2-9b-it', label: 'Gemma 2 9B IT' },
  ],
  openrouter: [
    { value: 'openrouter/auto', label: 'Auto Router (best available model)' },
    { value: 'google/gemma-4-31b-it:free', label: 'Google Gemma 4 31B (Free)' },
    { value: 'openai/gpt-oss-20b:free', label: 'OpenAI GPT-OSS 20B (Free)' },
    { value: 'anthropic/claude-3.5-sonnet', label: 'Claude 3.5 Sonnet' },
  ],
  gemini: [
    { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash (Recommended)' },
    { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro (Advanced Reasoning)' },
    { value: 'gemini-1.5-flash', label: 'Gemini 1.5 Flash' },
    { value: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro' },
  ],
};

const AIConfigDashboard = () => {
  const [activeTab, setActiveTab] = useState('identity');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [improving, setImproving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [testResult, setTestResult] = useState(null); // { success, error, warning, provider, model }
  const [customModelName, setCustomModelName] = useState('');
  const [knowledgeDocs, setKnowledgeDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [chatHistory, setChatHistory] = useState([
    { role: 'bot', content: 'Hello 👋\n\nWelcome! How may I help you today?' }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [isBotTyping, setIsBotTyping] = useState(false);
  const fileInputRef = useRef(null);
  const faqFileInputRef = useRef(null);
  
  const [websites, setWebsites] = useState([{ id: 1, url: 'https://meridatechminds.com' }]);
  const [faqs, setFaqs] = useState([{ id: 1, q: 'What are your working hours?' }]);
  
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [modalInput, setModalInput] = useState('');
  const [sourceType, setSourceType] = useState('file');

  const [config, setConfig] = useState({
    assistant_name: 'Merida AI',
    business_name: 'Merida Tech Minds',
    personality: 'Support',
    tone: 'friendly',
    primary_language: 'en',
    prompt: 'You are a helpful customer support agent for Merida Tech Minds.',
    system_prompt: '',
    context_window_size: 15,
    fallback_rule: 'transfer',
    confidence_threshold: 70,
    human_handoff_alert: true,
    llm_provider: 'openai',
    model_name: 'gpt-4o-mini',
    api_key: '',
  });

  const [behaviors, setBehaviors] = useState([
    { id: 1, name: 'Greeting Message', desc: 'Send a proactive greeting when chat opens', active: true },
    { id: 2, name: 'Suggested Replies', desc: 'Show quick reply buttons to users', active: true },
    { id: 3, name: 'Typing Indicator', desc: 'Simulate human typing delay', active: true },
    { id: 4, name: 'Emoji Usage', desc: 'Allow AI to use emojis naturally', active: true },
  ]);

  const [fields, setFields] = useState([
    { id: 1, name: 'Collect Name', active: true },
    { id: 2, name: 'Collect Phone', active: true },
    { id: 3, name: 'Collect Email', active: true },
    { id: 4, name: 'Collect Company', active: false },
  ]);

  const [rules, setRules] = useState([
    { id: 1, keyword: 'Pricing', reply: 'Our pricing starts at...', active: true },
    { id: 2, keyword: 'Location', reply: 'We are located at...', active: true },
  ]);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await apiClient.get('/whatsapp/ai-config/');
      const data = response.data;
      
      // Determine if model_name is a custom one
      const provider = data.llm_provider || 'openai';
      const standardModels = (MODEL_OPTIONS_BY_PROVIDER[provider] || []).map(m => m.value);
      const isCustom = data.model_name && !standardModels.includes(data.model_name);
      
      if (isCustom) {
        setCustomModelName(data.model_name);
        setConfig(prev => ({ ...prev, ...data, model_name: 'custom' }));
      } else {
        setConfig(prev => ({ ...prev, ...data }));
      }

      if (data.conversation_behaviours?.length) setBehaviors(data.conversation_behaviours);
      if (data.lead_collection?.length) setFields(data.lead_collection);
      if (data.auto_replies?.length) setRules(data.auto_replies);
      if (data.websites?.length) setWebsites(data.websites);
      if (data.faqs?.length) setFaqs(data.faqs);
      
      const docsResponse = await apiClient.get('/knowledge-documents/');
      setKnowledgeDocs(docsResponse.data || []);
    } catch (err) {
      console.error("Error fetching AI config:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const resolvedModelName = config.model_name === 'custom' ? customModelName : config.model_name;
      const payload = {
        ...config,
        llm_provider: (config.llm_provider || '').trim().toLowerCase() || undefined,
        model_name: (resolvedModelName || '').trim() || undefined,
        api_key: (config.api_key || '').trim(),
        conversation_behaviours: behaviors.map(b => ({ ...b, rule: `${b.name}: ${b.desc}`, is_active: b.active })),
        lead_collection: fields.map(f => ({ ...f, field_name: f.name, is_active: f.active })),
        auto_replies: rules,
        websites: websites,
        faqs: faqs,
      };
      await apiClient.post('/whatsapp/ai-config/', payload);
      alert('AI Configuration published successfully!');
    } catch (err) {
      console.error("Error saving AI config:", err.response?.data || err);
      alert(`Failed to save configuration: ${JSON.stringify(err.response?.data || err.message)}`);
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    setTestResult(null);
    try {
      const resolvedModelName = config.model_name === 'custom' ? customModelName : config.model_name;
      const response = await apiClient.post('/whatsapp/ai-config/test-connection/', {
        llm_provider: (config.llm_provider || '').trim().toLowerCase(),
        model_name: (resolvedModelName || '').trim(),
        api_key: (config.api_key || '').trim(),
      });
      setTestResult(response.data);
    } catch (err) {
      setTestResult({ success: false, error: err.response?.data?.error || 'Could not run the test - please try again.' });
    } finally {
      setTestingConnection(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (['llm_provider', 'model_name', 'api_key'].includes(name)) setTestResult(null);
    setConfig(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const insertVar = (variable) => {
    setConfig(prev => ({
      ...prev,
      system_prompt: (prev.system_prompt || '') + variable
    }));
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const response = await apiClient.post('/whatsapp/ai-prompt/generate/', {
        business_name: config.business_name,
        tone: config.tone,
      });
      setConfig(prev => ({ ...prev, system_prompt: response.data.generated_prompt }));
    } catch (err) {
      console.error(err);
      alert('Failed to generate prompt');
    } finally {
      setGenerating(false);
    }
  };

  const handleImprove = async () => {
    if (!config.system_prompt) return alert('Please enter a prompt to improve.');
    setImproving(true);
    try {
      const response = await apiClient.post('/whatsapp/ai-prompt/improve/', {
        prompt: config.system_prompt,
      });
      setConfig(prev => ({ ...prev, system_prompt: response.data.improved_prompt }));
    } catch (err) {
      console.error(err);
      alert('Failed to improve prompt');
    } finally {
      setImproving(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_name', file.name);

    setUploading(true);
    try {
      const response = await apiClient.post('/knowledge-documents/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setKnowledgeDocs(prev => [...prev, response.data]);
      
      // Trigger rebuild proxy for file
      const rebuildData = new FormData();
      rebuildData.append('file', file);
      await apiClient.post('/whatsapp/knowledge/rebuild/', rebuildData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      setModalVisible(false);
    } catch (err) {
      console.error("Upload/rebuild error", err);
      alert('Failed to upload document');
    } finally {
      setUploading(false);
      if (e.target) e.target.value = '';
    }
  };

  const handleFaqFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_name', file.name);

    setUploading(true);
    try {
      const response = await apiClient.post('/knowledge-documents/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setKnowledgeDocs(prev => [...prev, response.data]);

      // Trigger rebuild proxy for CSV/PDF FAQ file
      const rebuildData = new FormData();
      rebuildData.append('file', file);
      await apiClient.post('/whatsapp/knowledge/rebuild/', rebuildData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setModalVisible(false);
    } catch (err) {
      console.error("FAQ upload error", err);
      alert('Failed to upload FAQ document');
    } finally {
      setUploading(false);
      if (e.target) e.target.value = '';
    }
  };

  const handleDeleteDoc = async (id) => {
    try {
      await apiClient.delete(`/knowledge-documents/${id}/`);
      setKnowledgeDocs(prev => prev.filter(doc => doc.id !== id));
    } catch (err) {
      console.error("Delete error", err);
      alert('Failed to delete document');
    }
  };

  const renderPreview = (text) => {
    if (!text) return '';
    return text
      .replace(/{{customer_name}}/g, 'John Doe')
      .replace(/{{business_name}}/g, config.business_name || 'Your Business')
      .replace(/{{assistant_name}}/g, config.assistant_name || 'AI Assistant')
      .replace(/{{today}}/g, new Date().toLocaleDateString())
      .replace(/{{location}}/g, 'New York, USA')
      .replace(/{{phone}}/g, '+1234567890')
      .replace(/{{tone}}/g, config.tone || 'friendly');
  };

  const handleChatSubmit = async (e) => {
    if (e.key !== 'Enter' || !chatInput.trim()) return;

    const userMessage = chatInput.trim();
    setChatHistory(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatInput('');
    setIsBotTyping(true);

    try {
      const response = await apiClient.post('/whatsapp/ai-config/test-message/', { message: userMessage });
      setChatHistory(prev => [...prev, {
        role: 'bot',
        content: response.data?.answer || "I didn't get a response. Please try again."
      }]);
    } catch (err) {
      const errMsg = err?.response?.data?.error || 'AI test failed. Check your provider/API key in the settings above.';
      setChatHistory(prev => [...prev, { role: 'bot', content: `⚡ ï¸ ${errMsg}` }]);
    } finally {
      setIsBotTyping(false);
    }
  };

  const openModal = (type) => {
    setModalType(type);
    setSourceType('file');
    setModalInput('');
    setModalVisible(true);
  };

  const handleModalSave = async () => {
    if (!modalInput.trim()) return;
    
    const isWebsite = modalType === 'website' || (modalType === 'add_source' && sourceType === 'website');
    const isFaq = modalType === 'faq' || (modalType === 'add_source' && sourceType === 'faq');

    if (isWebsite) {
      setWebsites([...websites, { id: Date.now(), url: modalInput.trim() }]);
      try {
        await apiClient.post('/whatsapp/knowledge/rebuild/', { url: modalInput.trim() });
      } catch (err) {
        console.error("Error triggering URL rebuild:", err);
      }
    } else if (isFaq) {
      setFaqs([...faqs, { id: Date.now(), q: modalInput.trim() }]);
      try {
        await apiClient.post('/whatsapp/knowledge/rebuild/', { faq_q: modalInput.trim() });
      } catch (err) {
        console.error("Error triggering FAQ rebuild:", err);
      }
    }
    setModalVisible(false);
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading AI Configuration...</div>;
  }


  return (
    <div className="ai-config-dashboard">
      
      {/* Left Configuration Panel */}
      <div className="ai-config-left">
        
        {/* Main Header */}
        <div className="ai-header-container">
          <div>
            <h1 className="ai-header-title">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
              </svg>
              AI Configuration Studio
            </h1>
            <p className="ai-header-subtitle">Configure your AI Assistant without writing any code.</p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button 
                type="button"
                className="ai-btn ai-btn-ghost" 
                style={{ padding: '8px 14px', fontSize: '13px' }} 
                onClick={() => fetchConfig()}
              >
                Discard
              </button>
              <button 
                type="button"
                className="ai-btn ai-btn-primary" 
                style={{ padding: '8px 18px', fontSize: '13px', fontWeight: 600, background: '#6366f1', color: '#fff', borderRadius: '10px' }} 
                onClick={handleSave} 
                disabled={saving}
              >
                {saving ? 'Publishing...' : 'Publish Updates'}
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div className="ai-status-badge">
                <div className="ai-status-dot"></div>
                Active
              </div>
              <div className="ai-last-published">Last Published: Just now</div>
            </div>
          </div>
        </div>

        {/* SECTION 1: AI Identity Card */}
        <div className="ai-section">
          <h2 className="ai-section-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Bot size={22} style={{ color: '#6366f1' }} /> AI Identity
          </h2>
          <div className="ai-grid-2">
            <div className="ai-form-group">
              <label className="ai-label">Assistant Name</label>
              <input type="text" className="ai-input" name="assistant_name" value={config.assistant_name} onChange={handleChange} />
            </div>
            <div className="ai-form-group">
              <label className="ai-label">Business Name</label>
              <input type="text" className="ai-input" name="business_name" value={config.business_name} onChange={handleChange} />
            </div>
          </div>
          
          <div className="ai-grid-3">
            <div className="ai-form-group">
              <label className="ai-label">AI Personality</label>
              <input list="personality-list" className="ai-input" name="personality" value={config.personality} onChange={handleChange} placeholder="Type or select personality" />
              <datalist id="personality-list">
                <option value="Professional" />
                <option value="Friendly" />
                <option value="Sales" />
                <option value="Support" />
                <option value="Recruitment" />
                <option value="Finance" />
              </datalist>
            </div>
            <div className="ai-form-group">
              <label className="ai-label">Tone</label>
              <select className="ai-select" name="tone" value={config.tone} onChange={handleChange}>
                <option value="formal">Formal</option>
                <option value="friendly">Friendly</option>
                <option value="casual">Casual</option>
                <option value="expert">Expert</option>
              </select>
            </div>
            <div className="ai-form-group">
              <label className="ai-label">Primary Language</label>
              <select className="ai-select" name="primary_language" value={config.primary_language} onChange={handleChange}>
                <option value="en">English</option>
                <option value="ta">Tamil</option>
                <option value="hi">Hindi</option>
                <option value="te">Telugu</option>
                <option value="ml">Malayalam</option>
              </select>
            </div>
          </div>

          <div className="ai-form-group" style={{ marginTop: '16px' }}>
            <label className="ai-label">Description</label>
            <textarea className="ai-textarea" name="prompt" value={config.prompt} onChange={handleChange} placeholder="Briefly describe your AI's main purpose..." />
          </div>
        </div>

        {/* SECTION: LLM Model & Provider Configuration */}
        <div className="ai-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h2 className="ai-section-title" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Cpu size={22} style={{ color: '#0891b2' }} /> LLM Provider & Model Settings
            </h2>
            <span style={{ fontSize: '12px', background: '#e0e7ff', color: '#4338ca', padding: '4px 10px', borderRadius: '12px', fontWeight: 600 }}>
              SaaS Custom Key Freedom
            </span>
          </div>
          <p style={{ fontSize: '13px', color: 'var(--text-gray, #64748b)', margin: '0 0 20px 0' }}>
            Select your preferred AI Provider (OpenAI, Groq, OpenRouter, Google Gemini), choose the model, and enter your own API Key to avoid relying on hardcoded system defaults.
          </p>

          <div className="ai-grid-3">
            <div className="ai-form-group">
              <label className="ai-label">LLM Provider</label>
              <select 
                className="ai-select" 
                name="llm_provider" 
                value={config.llm_provider || 'openai'} 
                onChange={(e) => {
                  const newProvider = e.target.value;
                  const defaultModel = MODEL_OPTIONS_BY_PROVIDER[newProvider]?.[0]?.value || 'gpt-4o-mini';
                  setTestResult(null);
                  setConfig(prev => ({
                    ...prev,
                    llm_provider: newProvider,
                    model_name: defaultModel
                  }));
                }}
              >
                <option value="openai">OpenAI</option>
                <option value="groq">Groq</option>
                <option value="openrouter">OpenRouter</option>
                <option value="gemini">Google Gemini</option>
              </select>
            </div>

            <div className="ai-form-group">
              <label className="ai-label">Model Name</label>
              <select 
                className="ai-select" 
                name="model_name" 
                value={config.model_name || ''} 
                onChange={handleChange}
              >
                {(MODEL_OPTIONS_BY_PROVIDER[config.llm_provider || 'openai'] || []).map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
                <option value="custom">Custom Model Name...</option>
              </select>
            </div>

            <div className="ai-form-group">
              <label className="ai-label">API Key</label>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <input 
                  type={showApiKey ? 'text' : 'password'} 
                  className="ai-input" 
                  name="api_key" 
                  value={config.api_key || ''} 
                  onChange={handleChange} 
                  placeholder={`Enter your ${(config.llm_provider || 'OpenAI').toUpperCase()} API Key...`}
                  style={{ paddingRight: '40px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: '#64748b'
                  }}
                  title={showApiKey ? "Hide API Key" : "Show API Key"}
                >
                  {showApiKey ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
          </div>

          {config.model_name === 'custom' && (
            <div className="ai-form-group" style={{ marginTop: '16px' }}>
              <label className="ai-label">Custom Model Identifier</label>
              <input
                type="text"
                className="ai-input"
                name="custom_model_name"
                value={customModelName}
                placeholder="e.g. gpt-4o-2024-08-06, gemini-2.5-flash-lite, or llama-3.2-11b-vision-instruct"
                onChange={(e) => setCustomModelName(e.target.value)}
                autoFocus
              />
              {customModelName && (
                <span style={{ fontSize: '12px', color: '#64748b', marginTop: '4px', display: 'block' }}>
                  Will be saved as: <strong>{customModelName}</strong>
                </span>
              )}
            </div>
          )}

          <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={handleTestConnection}
              disabled={testingConnection || !config.api_key || !config.model_name}
              style={{
                padding: '10px 16px', borderRadius: '10px', border: '1px solid #c7d2fe',
                background: testingConnection ? '#eef2ff' : '#fff', color: '#4338ca', fontWeight: 600,
                fontSize: '13px', cursor: (testingConnection || !config.api_key || !config.model_name) ? 'not-allowed' : 'pointer',
                opacity: (!config.api_key || !config.model_name) ? 0.5 : 1,
              }}
            >
              {testingConnection ? 'Testing...' : 'Test Connection'}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: '10px 18px', borderRadius: '10px', border: 'none',
                background: '#6366f1', color: '#fff', fontWeight: 600,
                fontSize: '13px', cursor: saving ? 'not-allowed' : 'pointer',
                boxShadow: '0 2px 8px rgba(99, 102, 241, 0.25)',
              }}
            >
              {saving ? 'Publishing...' : 'Save & Publish AI Model'}
            </button>
            <span style={{ fontSize: '12px', color: '#64748b' }}>
              Checks that this provider, model, and API key actually work before you publish - catches an invalid key or a deprecated model immediately instead of silently failing on real messages.
            </span>
          </div>

          {testResult && (
            <div style={{
              marginTop: '12px', padding: '12px 14px', borderRadius: '10px', fontSize: '13px',
              background: testResult.success ? (testResult.warning ? '#fffbeb' : '#f0fdf4') : '#fef2f2',
              border: `1px solid ${testResult.success ? (testResult.warning ? '#fde68a' : '#bbf7d0') : '#fecaca'}`,
              color: testResult.success ? (testResult.warning ? '#92400e' : '#166534') : '#991b1b',
            }}>
              {testResult.success
                ? (testResult.warning ? `⚡ Connected, but: ${testResult.warning}` : `✓ Connected successfully - ${testResult.model} responded.`)
                : `✗ ${testResult.error}`}
            </div>
          )}
        </div>

        {/* SECTION 2: SYSTEM PROMPT */}
        <div className="ai-section">
          <h2 className="ai-section-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Keyboard size={22} style={{ color: '#6366f1' }} /> System Prompt
          </h2>
          <div className="ai-code-editor">
            <div className="ai-code-header">
              <div className="ai-code-dots">
                <div className="ai-code-dot red"></div>
                <div className="ai-code-dot yellow"></div>
                <div className="ai-code-dot green"></div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button className="ai-btn ai-btn-ghost" style={{ padding: '4px 12px', fontSize: '12px' }} onClick={handleGenerate} disabled={generating}>
                  {generating ? 'Generating...' : 'Generate'}
                </button>
                <button className="ai-btn ai-btn-ghost" style={{ padding: '4px 12px', fontSize: '12px' }} onClick={handleImprove} disabled={improving}>
                  {improving ? 'Improving...' : 'Improve'}
                </button>
                <button className="ai-btn ai-btn-primary" style={{ padding: '4px 12px', fontSize: '12px' }} onClick={() => setPreviewMode(!previewMode)}>
                  {previewMode ? 'Edit Mode' : 'Preview'}
                </button>
              </div>
            </div>
            <textarea 
              className="ai-code-textarea" 
              name="system_prompt"
              value={previewMode ? renderPreview(config.system_prompt) : config.system_prompt}
              onChange={handleChange}
              disabled={previewMode}
              placeholder={`You are the official AI Assistant for {{business_name}}.\nYour name is {{assistant_name}}.\n\nAlways be polite and use a {{tone}} tone.\nToday's date is {{today}}.\n\nWhen asking for details, collect them one by one.\nIf you don't know the answer, say so.`}
            />
            <div className="ai-prompt-vars">
              <span className="ai-var-tag" onClick={() => insertVar('{{customer_name}}')} style={{ cursor: 'pointer' }}>{`{{customer_name}}`}</span>
              <span className="ai-var-tag" onClick={() => insertVar('{{business_name}}')} style={{ cursor: 'pointer' }}>{`{{business_name}}`}</span>
              <span className="ai-var-tag" onClick={() => insertVar('{{assistant_name}}')} style={{ cursor: 'pointer' }}>{`{{assistant_name}}`}</span>
              <span className="ai-var-tag" onClick={() => insertVar('{{today}}')} style={{ cursor: 'pointer' }}>{`{{today}}`}</span>
              <span className="ai-var-tag" onClick={() => insertVar('{{location}}')} style={{ cursor: 'pointer' }}>{`{{location}}`}</span>
              <span className="ai-var-tag" onClick={() => insertVar('{{phone}}')} style={{ cursor: 'pointer' }}>{`{{phone}}`}</span>
            </div>
          </div>
        </div>

        {/* SECTION 3: Knowledge Sources */}
        <div className="ai-section">
          <h2 className="ai-section-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <BookOpen size={22} style={{ color: '#6366f1' }} /> Knowledge Sources
          </h2>
          <div className="ai-grid-3">
            <div className="ai-stat-card" style={{ cursor: 'pointer' }} onClick={() => openModal('website')}>
              <div className="ai-stat-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Globe size={24} style={{ color: '#6366f1' }} />
              </div>
              <h4 className="ai-stat-title">Website</h4>
              <p className="ai-stat-value">Connected ({websites.length} pages)</p>
            </div>
            <div className="ai-stat-card" style={{ cursor: 'pointer' }} onClick={() => fileInputRef.current?.click()}>
              <div className="ai-stat-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FileText size={24} style={{ color: '#6366f1' }} />
              </div>
              <h4 className="ai-stat-title">PDF Files</h4>
              <p className="ai-stat-value">{knowledgeDocs.length} Files</p>
            </div>
            <div className="ai-stat-card" style={{ cursor: 'pointer' }} onClick={() => openModal('faq')}>
              <div className="ai-stat-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <HelpCircle size={24} style={{ color: '#6366f1' }} />
              </div>
              <h4 className="ai-stat-title">FAQs</h4>
              <p className="ai-stat-value">{faqs.length} Questions</p>
            </div>
            <div className="ai-stat-card" style={{ cursor: 'pointer' }} onClick={() => alert('Product sync coming soon!')}>
              <div className="ai-stat-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Package size={24} style={{ color: '#6366f1' }} />
              </div>
              <h4 className="ai-stat-title">Products</h4>
              <p className="ai-stat-value">325 Products</p>
            </div>
            <div className="ai-stat-card" style={{ cursor: 'pointer' }} onClick={() => fileInputRef.current?.click()}>
              <div className="ai-stat-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ShieldCheck size={24} style={{ color: '#6366f1' }} />
              </div>
              <h4 className="ai-stat-title">Policies</h4>
              <p className="ai-stat-value">Click to upload</p>
            </div>
          </div>
          <div className="ai-upload-area" style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }} onClick={() => openModal('add_source')}>
            <div className="ai-upload-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '8px' }}>
              <UploadCloud size={28} style={{ color: '#6366f1' }} />
            </div>
            <h4 style={{ margin: '0 0 8px 0', color: 'var(--text-dark, #0f172a)' }}>Add Knowledge Source</h4>
            <p style={{ margin: 0, color: 'var(--text-gray, #64748b)', fontSize: '13px' }}>Click to add a file, website URL, or FAQ</p>
          </div>
          
          {knowledgeDocs.length > 0 && (
            <div style={{ marginTop: '20px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '500', marginBottom: '12px' }}>Uploaded Documents</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {knowledgeDocs.map(doc => (
                  <div key={doc.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontSize: '20px' }}>📄</span>
                      <div>
                        <div style={{ fontWeight: '500', color: '#0f172a' }}>{doc.file_name}</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>{new Date(doc.uploaded_at).toLocaleDateString()}</div>
                      </div>
                    </div>
                    <button 
                      onClick={() => handleDeleteDoc(doc.id)}
                      style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '6px', borderRadius: '4px' }}
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {websites.length > 0 && (
            <div style={{ marginTop: '20px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '500', marginBottom: '12px' }}>Connected Websites</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {websites.map(site => (
                  <div key={site.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <Globe size={20} style={{ color: '#6366f1' }} />
                      <div>
                        <div style={{ fontWeight: '500', color: '#0f172a' }}>{site.url}</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>Web Source</div>
                      </div>
                    </div>
                    <button 
                      onClick={() => setWebsites(websites.filter(w => w.id !== site.id))}
                      style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '6px', borderRadius: '4px' }}
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {faqs.length > 0 && (
            <div style={{ marginTop: '20px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '500', marginBottom: '12px' }}>Added FAQs</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {faqs.map(faq => (
                  <div key={faq.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <HelpCircle size={20} style={{ color: '#6366f1' }} />
                      <div>
                        <div style={{ fontWeight: '500', color: '#0f172a' }}>{faq.q}</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>FAQ Item</div>
                      </div>
                    </div>
                    <button 
                      onClick={() => setFaqs(faqs.filter(f => f.id !== faq.id))}
                      style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '6px', borderRadius: '4px' }}
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* SECTION 4: Conversation Behaviour */}
        <div className="ai-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 className="ai-section-title" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Cpu size={22} style={{ color: '#6366f1' }} /> Conversation Behaviour
            </h2>
            <button className="ai-btn ai-btn-secondary" style={{ padding: '6px 12px', fontSize: '13px' }} onClick={() => setBehaviors([...behaviors, { id: Date.now(), name: 'New Behaviour', desc: 'Description here', active: true }])}>+ Add Behaviour</button>
          </div>
          <div className="ai-grid-2">
            {behaviors.map((b, idx) => (
              <div className="ai-toggle-card" key={b.id || idx}>
                <div className="ai-toggle-info">
                  <input type="text" className="ai-input" value={b.name} onChange={(e) => {
                    const newB = [...behaviors];
                    newB[idx].name = e.target.value;
                    setBehaviors(newB);
                  }} style={{ padding: '4px 8px', fontSize: '14px', fontWeight: 600, border: 'none', background: 'transparent' }} />
                  <input type="text" className="ai-input" value={b.desc} onChange={(e) => {
                    const newB = [...behaviors];
                    newB[idx].desc = e.target.value;
                    setBehaviors(newB);
                  }} style={{ padding: '4px 8px', fontSize: '12px', border: 'none', background: 'transparent', color: 'var(--text-gray)' }} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <label className="ai-switch">
                    <input type="checkbox" checked={b.active} onChange={(e) => {
                      const newB = [...behaviors];
                      newB[idx].active = e.target.checked;
                      setBehaviors(newB);
                    }} />
                    <span className="ai-slider"></span>
                  </label>
                  <button 
                    onClick={() => setBehaviors(behaviors.filter((_, i) => i !== idx))}
                    style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}
                    title="Delete Behaviour"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="ai-form-group" style={{ marginTop: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <label className="ai-label">Context Window Size</label>
              <span className="ai-label">{config.context_window_size} Messages</span>
            </div>
            <input type="range" name="context_window_size" min="1" max="50" value={config.context_window_size} onChange={handleChange} style={{ width: '100%', accentColor: '#6366f1' }} />
          </div>
        </div>

        {/* SECTION 5: Lead Collection */}
        <div className="ai-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 className="ai-section-title" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <BookOpen size={22} style={{ color: '#6366f1' }} /> Lead Collection
            </h2>
            <button className="ai-btn ai-btn-secondary" style={{ padding: '6px 12px', fontSize: '13px' }} onClick={() => setFields([...fields, { id: Date.now(), name: 'New Field', active: true }])}>+ Add Field</button>
          </div>
          <div className="ai-grid-2">
            {fields.map((f, idx) => (
              <div className="ai-toggle-card" key={f.id || idx}>
                <div className="ai-toggle-info">
                  <input type="text" className="ai-input" value={f.name} onChange={(e) => {
                    const newF = [...fields];
                    newF[idx].name = e.target.value;
                    setFields(newF);
                  }} style={{ padding: '4px 8px', fontSize: '14px', fontWeight: 600, border: 'none', background: 'transparent' }} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <label className="ai-switch">
                    <input type="checkbox" checked={f.active} onChange={(e) => {
                      const newF = [...fields];
                      newF[idx].active = e.target.checked;
                      setFields(newF);
                    }} />
                    <span className="ai-slider"></span>
                  </label>
                  <button 
                    onClick={() => setFields(fields.filter((_, i) => i !== idx))}
                    style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}
                    title="Delete Field"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>


        {/* SECTION 8: Fallback Rules */}
        <div className="ai-section">
          <h2 className="ai-section-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ShieldCheck size={22} style={{ color: '#6366f1' }} /> Fallback Rules
          </h2>
          <div className="ai-grid-2">
            <div className="ai-form-group">
              <label className="ai-label">If AI doesn't know the answer</label>
              <select className="ai-select" name="fallback_rule" value={config.fallback_rule} onChange={handleChange}>
                <option value="reply">Apologize & ask to rephrase</option>
                <option value="transfer">Transfer to Human Agent</option>
                <option value="collect">Collect details & say we will get back</option>
              </select>
            </div>
            <div className="ai-form-group">
              <label className="ai-label">Confidence Threshold ({config.confidence_threshold}%)</label>
              <input type="range" name="confidence_threshold" min="1" max="100" value={config.confidence_threshold} onChange={handleChange} style={{ width: '100%', accentColor: '#6366f1', marginTop: '12px' }} />
            </div>
          </div>
          <div className="ai-toggle-card" style={{ marginTop: '16px' }}>
            <div className="ai-toggle-info">
              <h4>Human Handoff Alert</h4>
              <p>Notify team via email/push when human intervention is needed</p>
            </div>
            <label className="ai-switch">
              <input type="checkbox" name="human_handoff_alert" checked={config.human_handoff_alert} onChange={handleChange} />
              <span className="ai-slider"></span>
            </label>
          </div>
        </div>

        {/* SECTION 9: Auto Reply Rules (View Only) */}
        <div className="ai-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 className="ai-section-title" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Keyboard size={22} style={{ color: '#6366f1' }} /> Keyword Auto Replies
            </h2>
            <button className="ai-btn ai-btn-secondary" style={{ padding: '6px 12px', fontSize: '13px' }} onClick={() => setRules([...rules, { id: Date.now(), keyword: 'Keyword', reply: 'Reply...', active: true }])}>+ Add Rule</button>
          </div>
          <table className="ai-modern-table">
            <thead>
              <tr>
                <th>Keyword</th>
                <th>Reply Template</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((r, idx) => (
                <tr key={r.id || idx}>
                  <td>
                    <input type="text" className="ai-input" value={r.keyword} onChange={(e) => {
                      const newR = [...rules];
                      newR[idx].keyword = e.target.value;
                      setRules(newR);
                    }} style={{ width: '120px' }} />
                  </td>
                  <td>
                    <input type="text" className="ai-input" value={r.reply} onChange={(e) => {
                      const newR = [...rules];
                      newR[idx].reply = e.target.value;
                      setRules(newR);
                    }} style={{ width: '100%' }} />
                  </td>
                  <td>
                    <label className="ai-switch" style={{ margin: 0 }}>
                      <input type="checkbox" checked={r.active} onChange={(e) => {
                        const newR = [...rules];
                        newR[idx].active = e.target.checked;
                        setRules(newR);
                      }} />
                      <span className="ai-slider"></span>
                    </label>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>


      </div>

      {/* Right Fixed Panel - Live Preview */}
      <div className="ai-config-right">
        <div className="ai-chat-preview">
          <div className="ai-chat-header">
            <div className="ai-chat-avatar">M</div>
            <div>
              <h3 className="ai-chat-title">Merida AI</h3>
              <p className="ai-chat-subtitle">● Online</p>
            </div>
          </div>
          <div className="ai-chat-body" style={{ overflowY: 'auto', maxHeight: '400px' }}>
            <div style={{ alignSelf: 'center', fontSize: '11px', color: '#94a3b8', margin: '8px 0' }}>Today 10:42 AM</div>
            {chatHistory.map((msg, i) => (
              <div key={i} className={`ai-chat-bubble ${msg.role === 'user' ? 'ai-chat-user' : 'ai-chat-bot'}`}>
                {msg.content.split('\n').map((line, j) => <React.Fragment key={j}>{line}<br/></React.Fragment>)}
              </div>
            ))}
            {isBotTyping && (
              <div className="ai-chat-bubble ai-chat-bot">
                <div className="ai-chat-typing">
                  <div className="ai-typing-dot"></div>
                  <div className="ai-typing-dot"></div>
                  <div className="ai-typing-dot"></div>
                </div>
              </div>
            )}
          </div>
          <div className="ai-chat-footer">
            <input 
              type="text" 
              className="ai-chat-input" 
              placeholder="Type a message to test..." 
              value={chatInput}
              onChange={e => setChatInput(e.target.value)}
              onKeyDown={handleChatSubmit}
            />
          </div>
        </div>
      </div>

      {/* Bottom Action Bar */}
      <div className="ai-bottom-bar">
        <button className="ai-btn ai-btn-ghost" onClick={() => fetchConfig()}>Discard Changes</button>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button className="ai-btn ai-btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Publishing...' : 'Publish Updates'}</button>
        </div>
      </div>

      {modalVisible && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          backgroundColor: 'rgba(15, 23, 42, 0.4)',
          backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '16px',
            padding: '32px',
            width: '400px',
            maxWidth: '90%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '20px', fontWeight: 600, color: '#0f172a' }}>
              {modalType === 'website' ? 'Connect Website' : 
               modalType === 'faq' ? 'Add FAQ' : 
               'Add Knowledge Source'}
            </h3>
            
            {modalType === 'add_source' && (
              <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
                <div 
                  onClick={() => setSourceType('file')}
                  style={{ flex: 1, padding: '12px 8px', textAlign: 'center', border: sourceType === 'file' ? '2px solid #6366f1' : '1px solid #cbd5e1', borderRadius: '8px', cursor: 'pointer', background: sourceType === 'file' ? '#eef2ff' : '#fff' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '4px' }}><FileText size={20} /></div>
                  <div style={{ fontSize: '13px', fontWeight: 500, marginTop: '4px', color: sourceType === 'file' ? '#4f46e5' : '#475569' }}>File</div>
                </div>
                <div 
                  onClick={() => setSourceType('website')}
                  style={{ flex: 1, padding: '12px 8px', textAlign: 'center', border: sourceType === 'website' ? '2px solid #6366f1' : '1px solid #cbd5e1', borderRadius: '8px', cursor: 'pointer', background: sourceType === 'website' ? '#eef2ff' : '#fff' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '4px' }}><Globe size={20} /></div>
                  <div style={{ fontSize: '13px', fontWeight: 500, marginTop: '4px', color: sourceType === 'website' ? '#4f46e5' : '#475569' }}>Website</div>
                </div>
                <div 
                  onClick={() => setSourceType('faq')}
                  style={{ flex: 1, padding: '12px 8px', textAlign: 'center', border: sourceType === 'faq' ? '2px solid #6366f1' : '1px solid #cbd5e1', borderRadius: '8px', cursor: 'pointer', background: sourceType === 'faq' ? '#eef2ff' : '#fff' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '4px' }}><HelpCircle size={20} /></div>
                  <div style={{ fontSize: '13px', fontWeight: 500, marginTop: '4px', color: sourceType === 'faq' ? '#4f46e5' : '#475569' }}>FAQ</div>
                </div>
              </div>
            )}

            <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: '#64748b' }}>
              {(modalType === 'website' || (modalType === 'add_source' && sourceType === 'website')) ? 'Enter the URL of the website you want the AI to learn from.' : 
               (modalType === 'faq' || (modalType === 'add_source' && sourceType === 'faq')) ? 'Enter a frequently asked question to train the AI.' :
               'Upload documents (PDF, TXT, DOCX) to train your AI.'}
            </p>

            {(modalType === 'add_source' && sourceType === 'file') ? (
              <div className="ai-upload-area" style={{ position: 'relative', marginTop: 0, marginBottom: '24px' }} onClick={() => fileInputRef.current?.click()}>
                {uploading ? (
                  <div style={{ padding: '20px' }}>Uploading document...</div>
                ) : (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'center', margin: '0 auto 12px auto' }}><UploadCloud size={28} style={{ color: '#6366f1' }} /></div>
                    <h4 style={{ margin: '0 0 8px 0', color: 'var(--text-dark, #0f172a)' }}>Select File</h4>
                    <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>Supports PDF, TXT, DOCX (Max 10MB)</p>
                  </>
                )}
              </div>
            ) : (
              <>
                <input 
                  type="text" 
                  autoFocus
                  value={modalInput}
                  onChange={(e) => setModalInput(e.target.value)}
                  placeholder={(modalType === 'website' || (modalType === 'add_source' && sourceType === 'website')) ? 'https://example.com' : 'e.g. What are your hours?'}
                  style={{
                    width: '100%', padding: '12px 16px', fontSize: '14px',
                    border: '1px solid #cbd5e1', borderRadius: '8px', marginBottom: '16px',
                    outline: 'none', transition: 'border-color 0.2s', boxSizing: 'border-box'
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && handleModalSave()}
                />
                {(modalType === 'faq' || (modalType === 'add_source' && sourceType === 'faq')) && (
                  <div style={{ marginBottom: '24px' }}>
                    <div style={{ textAlign: 'center', margin: '8px 0 16px 0', color: '#94a3b8', fontSize: '13px', fontWeight: 500 }}>OR</div>
                    <div 
                      onClick={() => faqFileInputRef.current?.click()}
                      style={{ 
                        border: '1px dashed #cbd5e1', borderRadius: '8px', padding: '16px', 
                        textAlign: 'center', cursor: 'pointer', background: '#f8fafc',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                    >
                      <FolderOpen size={20} style={{ color: '#6366f1' }} />
                      <div style={{ fontSize: '13px', color: '#475569', fontWeight: 500 }}>Upload FAQ Document (CSV, PDF)</div>
                    </div>
                  </div>
                )}
              </>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button 
                onClick={() => setModalVisible(false)}
                style={{
                  padding: '10px 20px', background: 'transparent', border: '1px solid #cbd5e1',
                  borderRadius: '8px', color: '#475569', fontSize: '14px', fontWeight: 500, cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              {!(modalType === 'add_source' && sourceType === 'file') && (
                <button 
                  onClick={handleModalSave}
                  style={{
                    padding: '10px 20px', background: '#6366f1', border: 'none',
                    borderRadius: '8px', color: '#ffffff', fontSize: '14px', fontWeight: 500, cursor: 'pointer',
                    boxShadow: '0 4px 6px -1px rgba(99, 102, 241, 0.2)'
                  }}
                >
                  Save {(modalType === 'website' || (modalType === 'add_source' && sourceType === 'website')) ? 'URL' : 'FAQ'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      <input 
        type="file" 
        ref={fileInputRef} 
        onChange={handleFileUpload} 
        accept=".pdf,.txt,.docx,.doc,.docs,.rtf" 
        style={{ display: 'none' }} 
      />
      <input 
        type="file" 
        ref={faqFileInputRef} 
        onChange={handleFaqFileUpload} 
        accept=".csv,.pdf" 
        style={{ display: 'none' }} 
      />
    </div>
  );
};

export default AIConfigDashboard;



